// src\themes\lino\components\SwiperList.tsx
'use client';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Scrollbar } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/scrollbar';
import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'motion/react';
import { SwiperListProps } from '@/core/models/slider-model';
import { useDictionary } from '@/core/hooks/use-dictionary';
import { ArrowLeft } from 'lucide-react';
import { textGroupVariants, textItemVariants } from './animations/textReveal';

// وقتی آیتم‌ها کمن، عرض هر اسلاید بزرگ‌تر می‌شه تا ردیف رو مساوی پر کنه؛ ولی
// اگه جا نباشه (زوم بالا، صفحه‌ی باریک) به‌جای شکستن خط (که با CSS grid پیش
// می‌اومد)، به حداقل‌عرض می‌چسبه و خودِ سوییپر اسکرول‌پذیرش می‌کنه.
const MAX_VISIBLE = 5;
const MIN_ITEM_WIDTH = 150;
const GAP = 20;

export default function SwiperList({ data }: SwiperListProps) {
  const { dictionary } = useDictionary();

  const items = Array.isArray(data.items)
    ? data.items
    : Array.isArray(data.sliderItems)
      ? data.sliderItems
      : [];

  const visibleCount = Math.min(items.length, MAX_VISIBLE) || 1;
  const slideWidth = `max(${MIN_ITEM_WIDTH}px, calc((100% - ${(visibleCount - 1) * GAP}px) / ${visibleCount}))`;

  const renderItem = (item: (typeof items)[number], index: number) => {
    const imageUrl = item.image?.url || item.image_info?.url;
    const Content = (
      <div className="group">
        <div className="relative w-full aspect-[3/4] overflow-hidden rounded-2xl bg-primary-100 mb-3 border border-transparent group-hover:border-primary-200 transition-colors duration-500">
          {imageUrl && (
            <Image
              src={imageUrl}
              alt={item.title || ''}
              fill
              className="object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.035]"
            />
          )}
        </div>
        {item.title && (
          <h4 className="text-[12.5px] tracking-wide text-primary-900">{item.title}</h4>
        )}
        {item.description && (
          <p className="text-[12px] text-primary-500 mt-0.5">{item.description}</p>
        )}
      </div>
    );

    return item.link ? (
      <Link href={item.link} key={index}>
        {Content}
      </Link>
    ) : (
      <div key={index}>{Content}</div>
    );
  };

  return (
    <div className="w-full col-span-12">
      <motion.div
        className="flex justify-between items-end mb-8 pb-5 border-b border-primary-200 max-md:flex-col max-md:items-start gap-2"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false, amount: 0.4 }}
        variants={textGroupVariants}
      >
        <div className="space-y-1.5">
          <motion.h3 variants={textItemVariants} className="text-lg lg:text-xl font-light text-primary-900 tracking-tight">{data.title}</motion.h3>
          {data.description && (
            <motion.p variants={textItemVariants} className="text-[13px] text-primary-500">{data.description}</motion.p>
          )}
        </div>
        {data.link && (
          <motion.div variants={textItemVariants}>
            <Link className="text-[11px] uppercase tracking-[0.12em] text-primary-700 hover:text-primary-900 flex items-center gap-1.5 transition-colors" href={data.link}>
              {dictionary.ui?.pages.seeAll}
              <ArrowLeft className="stroke-[1.5] size-3.5 rtl:rotate-180" />
            </Link>
          </motion.div>
        )}
      </motion.div>

      <Swiper
        modules={[Scrollbar]}
        spaceBetween={GAP}
        slidesPerView="auto"
        scrollbar={{ draggable: true, dragSize: 100 }}
        className="w-full"
        style={{ paddingBottom: '40px' }}
      >
        {items.map((item, index) => (
          <SwiperSlide key={index} style={{ width: slideWidth }}>
            {renderItem(item, index)}
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}